import { LowerCasePipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EHelpCurationStatus, HelpCurationService, IHelpCurationItem } from '@insolution/help-data-access';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'lib-help-curation-list',
  imports: [FormsModule, TranslatePipe, LowerCasePipe],
  templateUrl: './help-curation-list.html',
  styleUrl: './help-curation-list.scss',
})
export class HelpCurationList {
  private readonly helpCurationService = inject(HelpCurationService);

  protected readonly eHelpCurationStatus = EHelpCurationStatus;
  protected readonly statuses = [
    EHelpCurationStatus.Pending,
    EHelpCurationStatus.Promoted,
    EHelpCurationStatus.Rejected,
  ];

  protected readonly selectedStatus = signal<EHelpCurationStatus>(EHelpCurationStatus.Pending);
  protected readonly items = signal<IHelpCurationItem[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly hasError = signal(false);
  protected readonly busyKey = signal<string | null>(null);

  // Per-item editable answer (seeded from the AI answer) and optional rejection reason.
  protected readonly drafts = signal<Record<string, string>>({});
  protected readonly reasons = signal<Record<string, string>>({});

  constructor() {
    void this.load();
  }

  protected key(item: IHelpCurationItem): string {
    return `${item.sessionId}#${item.turnId}`;
  }

  protected async selectStatus(status: EHelpCurationStatus): Promise<void> {
    if (status === this.selectedStatus()) {
      return;
    }
    this.selectedStatus.set(status);
    await this.load();
  }

  protected async load(): Promise<void> {
    this.isLoading.set(true);
    this.hasError.set(false);
    try {
      const items = await this.helpCurationService.list(this.selectedStatus());
      this.items.set(items);
      this.drafts.set(Object.fromEntries(items.map((item) => [this.key(item), item.answer])));
      this.reasons.set({});
    } catch {
      this.hasError.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  protected updateDraft(item: IHelpCurationItem, value: string): void {
    this.drafts.update((drafts) => ({ ...drafts, [this.key(item)]: value }));
  }

  protected updateReason(item: IHelpCurationItem, value: string): void {
    this.reasons.update((reasons) => ({ ...reasons, [this.key(item)]: value }));
  }

  protected async promote(item: IHelpCurationItem): Promise<void> {
    const answer = (this.drafts()[this.key(item)] ?? item.answer).trim();
    if (!answer) {
      return;
    }
    await this.runAction(item, () =>
      this.helpCurationService.promote({ sessionId: item.sessionId, turnId: item.turnId, answer })
    );
  }

  protected async reject(item: IHelpCurationItem): Promise<void> {
    const reason = this.reasons()[this.key(item)]?.trim() || undefined;
    await this.runAction(item, () =>
      this.helpCurationService.reject({ sessionId: item.sessionId, turnId: item.turnId, reason })
    );
  }

  private async runAction(item: IHelpCurationItem, action: () => Promise<void>): Promise<void> {
    this.busyKey.set(this.key(item));
    this.hasError.set(false);
    try {
      await action();
      await this.load();
    } catch {
      this.hasError.set(true);
    } finally {
      this.busyKey.set(null);
    }
  }
}
